import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Home, LogIn } from 'lucide-react';
import Popover from '../ui/Popover';

export type ClockLocation = 'office' | 'home';

const LOCATION_KEY = 'ote_clock_location';

/** The last location this browser clocked in from (listed first next time). */
export function readLocation(): ClockLocation {
  try {
    return localStorage.getItem(LOCATION_KEY) === 'home' ? 'home' : 'office';
  } catch {
    return 'office';
  }
}
/** Remember the chosen location (listed first next time). */
export function saveLocation(loc: ClockLocation) {
  try { localStorage.setItem(LOCATION_KEY, loc); } catch { /* private mode: not remembered */ }
}

/**
 * The two places, the last one used first. Shared by every clock-in choice
 * (this dropdown, the "עוד לא החתמת כניסה" note and the "להחתים כניסה
 * ולהתחיל?" question), so they always list them the same way.
 */
export function clockChoices(): { value: ClockLocation; Icon: typeof Building2 }[] {
  return readLocation() === 'home'
    ? [{ value: 'home', Icon: Home }, { value: 'office', Icon: Building2 }]
    : [{ value: 'office', Icon: Building2 }, { value: 'home', Icon: Home }];
}

interface ClockInChoiceProps {
  /** Classes of the trigger (the host's button style). */
  buttonClassName: string;
  pending: boolean;
  /** Called with the chosen location; the host runs the clock-in. */
  onPick: (loc: ClockLocation) => void;
  /** Called when the choice opens (e.g. clear an old error). */
  onOpen?: () => void;
  /** Which inline edge of the trigger the choice lines up with (default `start`). */
  align?: 'start' | 'end';
}

/**
 * "החתמת כניסה" + a small Liquid Glass dropdown with two buttons, משרד · בית
 * (owner, 2026-10-07: the location decides travel pay, so it is chosen on every
 * clock-in; the last choice is listed first). A press (click, tap, Enter, Space)
 * opens it and a second press closes it. There is no hover opening any more
 * (2026-10-11): the click that followed a hover used to close what the hover
 * had just opened. Used by the top-bar attendance control and the dashboard
 * attendance widget.
 */
export function ClockInChoice({ buttonClassName, pending, onPick, onOpen, align = 'start' }: ClockInChoiceProps) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const choices = clockChoices();

  const pick = (loc: ClockLocation) => {
    setOpen(false);
    saveLocation(loc);
    onPick(loc);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={buttonClassName}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => { if (!open) onOpen?.(); setOpen(!open); }}
        disabled={pending}
        data-clock-in-trigger=""
      >
        <LogIn size={16} className="rtl-mirror" aria-hidden />
        {t('home.attendance.clockInStart')}
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align={align}
        role="dialog"
        ariaLabel={t('home.attendance.whereToday')}
        focusFirst
        className="w-64 p-3"
      >
        <p className="mb-2 text-sm font-medium text-text">{t('home.attendance.whereToday')}</p>
        <div className="grid grid-cols-2 gap-2">
          {choices.map(({ value, Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => pick(value)}
              className="btn btn-secondary min-h-touch md:min-h-control flex-col gap-1 py-2"
              data-clock-location={value}
            >
              <Icon size={18} aria-hidden />
              <span>{t(`attendance.location_${value}`)}</span>
            </button>
          ))}
        </div>
      </Popover>
    </>
  );
}
